import { Target, Radar, Brain, ShieldCheck, TrendingUp, Users, Layers, Zap } from "lucide-react";
import ProductLandingTemplate from "../../components/marketing/ProductLandingTemplate";

export default function SynqProjectsPage() {
  const mockDashboard = (
    <div className="p-6 md:p-10 bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="col-span-2 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-6">
          <div className="text-xs font-ibm-plex uppercase tracking-widest opacity-70">PIQ Score</div>
          <div className="font-outfit text-6xl font-black mt-2">78</div>
          <div className="text-xs font-ibm-plex mt-1 opacity-70">Stable · Confidence: High</div>
        </div>
        {[
          {label: "Purpose",     val: 4.2, tone: "emerald"},
          {label: "Team Energy", val: 2.6, tone: "amber"},
          {label: "Ethics",      val: 4.0, tone: "emerald"},
          {label: "Adaptability",val: 3.4, tone: "blue"},
        ].map((p, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-xs font-ibm-plex text-slate-500">{p.label}</div>
            <div className="font-outfit text-2xl font-black text-slate-900 dark:text-white mt-1">{p.val}</div>
            <div className={`text-[10px] font-ibm-plex mt-2 uppercase tracking-widest ${
              p.tone==="emerald" ? "text-emerald-600" : p.tone==="amber" ? "text-amber-600" : "text-blue-600"
            }`}>{p.tone === "amber" ? "Instability" : p.tone === "blue" ? "Drift Watch" : "Stable"}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="text-xs font-ibm-plex uppercase tracking-widest text-blue-600 font-bold">Stabilization</div>
          <div className="font-outfit text-sm font-bold text-slate-900 dark:text-white mt-2">1 pillar shows instability — proactive stabilization recommended.</div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="text-xs font-ibm-plex uppercase tracking-widest text-blue-600 font-bold">Signal Confidence</div>
          <div className="font-outfit text-sm text-slate-900 dark:text-white mt-2">Strength: <b>High</b> · Confidence: <b>High</b> · Drift: <b>Stable</b></div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <div className="text-xs font-ibm-plex uppercase tracking-widest text-blue-600 font-bold">Reality Gap</div>
          <div className="font-outfit text-sm text-slate-900 dark:text-white mt-2">IC ↔ Manager gap: <b>0.4</b> · Aligned</div>
        </div>
      </div>
    </div>
  );

  return (
    <ProductLandingTemplate
      kicker="Project Intelligence Platform"
      name="SYNQ Projects"
      headline="Predict health, before it turns to hindsight."
      subhead="Project health, delivery confidence, execution quality and organizational capability — sensed continuously, quantified precisely, and surfaced as systemic action."
      accentClass="bg-blue-200 dark:bg-blue-900/50"
      vision={{
        title: "The end of blind delivery.",
        body: "Every project generates a real-time signal. SYNQ Projects listens across teams, rolls up pillar-level insight, spots the reality gap between contributors and leadership, and turns each observation into a systemic recommendation — not a personal report card.",
      }}
      whyMatters={[
        { title: "Most project reports arrive too late.",
          body: "By the time RAG-status turns red, the damage is done. SYNQ turns lagging indicators into leading intelligence." },
        { title: "Dashboards don't decide.",
          body: "SYNQ doesn't just visualise; it recommends. Every insight comes with a systemic, non-accusatory next action." },
        { title: "Signals get lost between layers.",
          body: "IC feedback, manager view, executive perception — SYNQ triangulates all three and highlights reality drift." },
        { title: "Assessment fatigue kills sensing.",
          body: "SYNQ learns from skipped pulses, delays and rotates questions — you sense more with fewer questions." },
      ]}
      capabilities={[
        { title: "Project Health & PIQ Score", body: "Aggregated 0–100 signal across seven organizational pillars.", icon: <Target className="w-5 h-5" /> },
        { title: "Delivery Confidence",         body: "Real-time risk of missed timeline, scope, or stakeholder alignment.", icon: <Zap className="w-5 h-5" /> },
        { title: "Execution Intelligence",      body: "Sprint-level insight into what's really slowing delivery.", icon: <Layers className="w-5 h-5" /> },
        { title: "Risk Prediction",             body: "Forward-looking, drift-triggered risk alerts before symptoms surface.", icon: <ShieldCheck className="w-5 h-5" /> },
        { title: "Stakeholder Intelligence",    body: "Reality Gap engine across IC, Manager and Executive viewpoints.", icon: <Users className="w-5 h-5" /> },
        { title: "Leadership Dashboards",       body: "Executive views built around narrative, not raw metrics.", icon: <TrendingUp className="w-5 h-5" /> },
        { title: "AI Recommendations",          body: "Claude-powered, systemic and non-accusatory next actions.", icon: <Brain className="w-5 h-5" /> },
        { title: "Adaptive Sensing",            body: "Fatigue-aware question rotation and pulse scheduling.", icon: <Radar className="w-5 h-5" /> },
      ]}
      howItWorks={[
        { title: "Configure the project", body: "Set the project stage (Pre-Project / Active / Sprint), role, financial toggle and cadence. SYNQ selects the right questionnaire dynamically." },
        { title: "Sense the signal",      body: "Contributors answer role-adjusted questions plus two open-ended prompts. Fatigue-aware rotation keeps the load light and useful." },
        { title: "Understand the pattern",body: "Five intelligence engines run in parallel: pillar scoring, stabilization, signal confidence, reality-gap classification, and LLM theme clustering." },
        { title: "Act with confidence",   body: "Every result page surfaces top actions and per-pillar recommendations — track, iterate and evolve." },
      ]}
      aiIntelligence={{
        title: "AI that recommends, not decides.",
        body: "SYNQ Projects pairs deterministic pillar scoring with a Claude-powered advisor. The AI reads the same signal an experienced director would — and produces systemic, non-accusatory guidance in seconds.",
        bullets: [
          "Claude Sonnet 4.5 via Emergent LLM key",
          "Open feedback clustered anonymously — never attributed to individuals",
          "Recommendations framed as organizational stabilization, never as blame",
        ],
      }}
      mockDashboard={mockDashboard}
      benefits={[
        "Catch delivery risk 2–4 weeks earlier than status reports.",
        "Reduce assessment fatigue while increasing signal quality.",
        "Align IC, Manager and Executive perception in one view.",
        "Turn insight into systemic action, never into finger-pointing.",
        "Export executive-ready PDF reports in a single click.",
        "Track the impact of stabilization actions over time.",
      ]}
      faq={[
        { q: "How is SYNQ Projects different from a project dashboard?",
          a: "Dashboards show what happened. SYNQ Projects tells you what to do next — and why. Every insight comes with a systemic action, framed to avoid blame." },
        { q: "Is contributor feedback ever attributed to individuals?",
          a: "Never. Feedback is stored anonymously, clustered by our LLM, and the sample quotes we surface only appear when at least three contributors share a theme." },
        { q: "Does experience-weighting distort scores?",
          a: "No. Experience only affects the signal-confidence layer, never the pillar scores. Junior contributor signals count fully." },
        { q: "How does the AI handle sensitive feedback?",
          a: "The AI reads only anonymized text, produces systemic theme clusters, and refuses to speculate about individuals. Human-safe framing is part of the prompt." },
        { q: "Can we export data?",
          a: "Yes — one-click PDF export, plus API access on Enterprise plans." },
      ]}
    />
  );
}
